import React, { useEffect, useState } from 'react';

export interface DataTileFieldConfig {
  key: string;
  label: string;
}

export interface DataTileConfig {
  titleField: string;
  fieldDisplay: {
    mode: 'explicit' | 'all';
    fields: DataTileFieldConfig[];
    additionalFields?: {
      mode: 'none' | 'all' | 'allowlist' | 'blocklist';
      fields?: string[];
    };
  };
}

export interface DataTileItem {
  [key: string]: any;
}

export interface DataTileProps {
  items: DataTileItem[];
  config?: DataTileConfig;
  footer?: (item: DataTileItem) => React.ReactNode;
  className?: string;
  rounded?: boolean;
}

const DataTile: React.FC<DataTileProps> = ({
  items = [],
  config,
  footer,
  className,
  rounded = false
}) => {
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('dark');

  // Theme detection
  useEffect(() => {
    const detectTheme = () => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setCurrentTheme(isDark ? 'dark' : 'light');
    };

    detectTheme();
    
    const observer = new MutationObserver(detectTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // Get fields to display based on configuration
  const getFieldsToDisplay = (item: DataTileItem): Array<{ key: string; label: string; value: any }> => {
    if (!config) {
      // Fallback to default fields if no config
      return [
        { key: 'type', label: 'Type', value: item.type || 'N/A' },
        { key: 'location', label: 'Location', value: item.location || 'N/A' },
        { key: 'status', label: 'Status', value: item.status || 'N/A' }
      ];
    }

    const fields: Array<{ key: string; label: string; value: any }> = [];
    
    // Add explicit fields
    config.fieldDisplay.fields.forEach(field => {
      const value = item[field.key];
      if (value !== undefined || config.fieldDisplay.mode === 'explicit') {
        fields.push({
          key: field.key,
          label: field.label,
          value: value || 'N/A'
        });
      }
    });

    // Handle additional fields
    if (config.fieldDisplay.additionalFields) {
      const { mode, fields: additionalFieldList } = config.fieldDisplay.additionalFields;
      const explicitKeys = new Set(config.fieldDisplay.fields.map(f => f.key));
      
      if (mode === 'all') {
        // Add all fields not already included
        Object.keys(item).forEach(key => {
          if (!explicitKeys.has(key) && key !== 'tags' && key !== '_sourceInfo' && !key.startsWith('_')) {
            fields.push({
              key,
              label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
              value: item[key] || 'N/A'
            });
          }
        });
      } else if (mode === 'allowlist' && additionalFieldList) {
        // Add specific additional fields
        additionalFieldList.forEach(key => {
          if (!explicitKeys.has(key) && item[key] !== undefined) {
            fields.push({
              key,
              label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
              value: item[key] || 'N/A'
            });
          }
        });
      } else if (mode === 'blocklist' && additionalFieldList) {
        // Add all fields except those in blocklist
        const blocklist = new Set(additionalFieldList);
        Object.keys(item).forEach(key => {
          if (!explicitKeys.has(key) && !blocklist.has(key) && key !== 'tags' && key !== '_sourceInfo' && !key.startsWith('_')) {
            fields.push({
              key,
              label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
              value: item[key] || 'N/A'
            });
          }
        });
      }
    }

    return fields;
  };

  // Get title based on configuration
  const getTitle = (item: DataTileItem): string => {
    if (!config || !config.titleField) {
      return item.title || 'Untitled';
    }
    return item[config.titleField] || 'Untitled';
  };

  // Format value for display
  const formatValue = (value: any): string => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    return String(value);
  };

  return (
    <div className={`core-data-tile-container ${className || ''}`}>
      {items.map((item, index) => {
        const fields = getFieldsToDisplay(item);
        const title = getTitle(item);
        
        return (
          <div 
            key={index} 
            className={`core-data-tile ${rounded ? 'core-data-tile--rounded' : ''}`}
          >
            {/* Header Row */}
            <div className="core-data-tile__header">
              <span className="core-data-tile__title">{title}</span>
            </div>
            
            {/* Content Row */}
            <div className="core-data-tile__content">
              <div className="core-data-tile__key-value-grid">
                {fields.map(field => (
                  <React.Fragment key={field.key}>
                    <div className="core-data-tile__key">{field.label}</div>
                    <div className="core-data-tile__value">
                      {formatValue(field.value)}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
            
            {/* Footer Row - Optional */}
            {footer && (
              <div className="core-data-tile__footer">
                {footer(item)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DataTile;